import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
  BellIcon,
  CakeIcon,
  SunIcon,
  ArrowUturnLeftIcon,
  GiftIcon,
} from '@heroicons/react/24/outline';
import { listUpcomingEvents, type UpcomingEvent } from '@/api/notifications';
import clsx from 'clsx';

const KIND_STYLE: Record<UpcomingEvent['kind'], { icon: typeof CakeIcon; chip: string }> = {
  birthday: { icon: CakeIcon, chip: 'bg-pink-50 text-pink-600' },
  time_off_start: { icon: SunIcon, chip: 'bg-amber-50 text-amber-600' },
  time_off_end: { icon: ArrowUturnLeftIcon, chip: 'bg-emerald-50 text-emerald-600' },
  holiday: { icon: GiftIcon, chip: 'bg-indigo-50 text-indigo-600' },
};

/** Topbar bell with a dropdown of upcoming events (birthdays, leaves, holidays). */
export function NotificationsDropdown() {
  const { t } = useTranslation(['notifications', 'holidays']);

  const { data: events = [] } = useQuery({
    queryKey: ['notifications', 14],
    queryFn: () => listUpcomingEvents(14),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  });

  const whenLabel = (e: UpcomingEvent): string => {
    if (e.days_until === 0) return t('notifications:when.today');
    if (e.days_until === 1) return t('notifications:when.tomorrow');
    return t('notifications:when.inDays', { count: e.days_until });
  };

  const eventText = (e: UpcomingEvent): string => {
    switch (e.kind) {
      case 'birthday':
        return t('notifications:events.birthday', { name: e.employee_name, turns: e.turns });
      case 'time_off_start':
        return t('notifications:events.timeOffStart', {
          name: e.employee_name,
          type: t(`holidays:timeOff.types.${e.time_off_type}`),
          end: e.end_date,
        });
      case 'time_off_end':
        return t('notifications:events.timeOffEnd', { name: e.employee_name });
      case 'holiday':
        return t('notifications:events.holiday', { name: e.holiday_name });
    }
  };

  return (
    <Popover>
      <PopoverButton
        className="relative inline-flex items-center justify-center rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
        aria-label={t('notifications:title')}
      >
        <BellIcon className="h-6 w-6" aria-hidden="true" />
        {events.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center leading-none ring-2 ring-white">
            {events.length > 99 ? '99+' : events.length}
          </span>
        )}
      </PopoverButton>

      <PopoverPanel
        anchor="bottom end"
        className="z-50 mt-1 w-96 max-w-[calc(100vw-1.5rem)] origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
      >
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-900">{t('notifications:title')}</p>
          <p className="text-xs text-gray-400 mt-0.5">{t('notifications:dropdownHint')}</p>
        </div>

        {events.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <BellIcon className="h-8 w-8 text-gray-300" aria-hidden="true" />
            <p className="text-sm text-gray-500">{t('notifications:empty.title')}</p>
          </div>
        ) : (
          <ul className="max-h-96 overflow-y-auto divide-y divide-gray-50 py-1">
            {events.map((e, idx) => {
              const { icon: Icon, chip } = KIND_STYLE[e.kind];
              const body = (
                <>
                  <span className={clsx('flex h-8 w-8 items-center justify-center rounded-full shrink-0', chip)}>
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-gray-900 leading-snug">{eventText(e)}</span>
                    <span className="block text-xs text-gray-400 mt-0.5 font-mono">{e.date}</span>
                  </span>
                  <span
                    className={clsx(
                      'shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap',
                      e.days_until === 0
                        ? 'bg-red-50 text-red-600'
                        : e.days_until <= 3
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-gray-100 text-gray-600',
                    )}
                  >
                    {whenLabel(e)}
                  </span>
                </>
              );
              const key = `${e.kind}-${e.date}-${e.employee_id ?? e.holiday_name ?? idx}`;
              return (
                <li key={key}>
                  {e.employee_id ? (
                    <Link
                      to={`/employees/${e.employee_id}`}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                    >
                      {body}
                    </Link>
                  ) : (
                    <span className="flex items-center gap-3 px-4 py-2.5">{body}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </PopoverPanel>
    </Popover>
  );
}
